import { useEffect, useRef, useState } from 'react';
import { Producto } from '../../models/Producto';

type InitialData = {
  productos: Producto[];
  totalPages: number;
  totalResults: number;
};

const RESULTS_PER_PAGE = 24;

// Layout genérico para grillas SEO (novedades, destacados, tematica/tipo/color por slug):
// solo scroll infinito, sin filtros de usuario (el filtro es fijo por ruta).
const useHook = (initial: InitialData, filtroFijo: Record<string, unknown>) => {

  const [productos, setProductos] = useState<Producto[]>(initial.productos);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(initial.totalPages);
  const fetchAbortRef = useRef<AbortController | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const fetchProductos = async (targetPage: number) => {
    fetchAbortRef.current?.abort();
    const abortController = new AbortController();
    fetchAbortRef.current = abortController;

    setLoadingMore(true);

    const body = {
      liviano: true,
      page: targetPage,
      pageSize: RESULTS_PER_PAGE,
      ...filtroFijo,
    };

    try {
      const result = await fetch(`${process.env.NEXT_PUBLIC_URL_FRONTEND}/api/productos/get-filtered`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: abortController.signal,
      });
      const data = await result.json();
      if (data?.data) {
        setProductos(prev => [...prev, ...data.data]);
        setTotalPages(Math.ceil(data?.meta?.pagination?.total / RESULTS_PER_PAGE));
      }
      setLoadingMore(false);
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
      console.log("e", err);
      setLoadingMore(false);
    }
  };

  // refs para que el observer del infinite scroll siempre lea el último estado sin recrearse
  const latestRef = useRef({ page, totalPages, loadingMore, fetchProductos });
  useEffect(() => {
    latestRef.current = { page, totalPages, loadingMore, fetchProductos };
  });

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver((entries) => {
      const { page, totalPages, loadingMore, fetchProductos } = latestRef.current;
      if (entries[0].isIntersecting && !loadingMore && page < totalPages) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchProductos(nextPage);
      }
    }, { rootMargin: '400px' });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return {
    productos,
    loadingMore,
    sentinelRef,
  };
};

export default useHook;
